import React, { FC, useEffect, useMemo } from "react";
import { loadCurrentListing } from "../../redux/actions/listingEntry/load-current-listing";
import { currentListingSelector } from "../../redux/selectors/entry/current-listing-selector";
import { useDispatch, useSelector } from "react-redux";
import ListingEntry from "./listing-entry";
import { RouteMatch } from "../../types/common/routes";
import { mainMessageSelector } from "../../redux/selectors/system/main-message-selector";
import { MainMessaging, State } from "../../types/state";
import { Listing } from "../../types/listing/listing";
import MainMessage from "../common/messages/main-message";
import LoadingContainer from "../../components/loader/loading-container";
import { reloadListingIdSelector } from "../../redux/selectors/system/reload-listing-id-selector";
import { setReloadListingId } from "../../redux/actions/system/set-reload-listing-id";
import { Config } from "../../types/config/config";
import { configSelector } from "../../redux/selectors/system/config-selector";
import { isDuplicatingListingSelector } from "../../redux/selectors/system/is-duplicating-listing-selector";
import { setIsDuplicatingListing } from "../../redux/actions/system/set-is-duplicating-listing";
import { loadExternalPublishedCount } from "../../redux/actions/pagedListings/load-external-published-count";
import MiqSyncReview from "../miq/miq-sync/miq-sync-review";
import { useMiqSync } from "../miq/miq-sync/use-miq-sync";
import { isMiqSyncEnabled } from "../../utils/config/miq-sync-enabled";

interface Props {
  match: RouteMatch;
}

const clearPendingImageDeletions = () => {
  if (typeof window !== "undefined") {
    (window as any).__pendingImageDeletions = [];
  }
};

const ListingEntryContainer: FC<Props> = (props) => {
  const dispatch = useDispatch();

  let navTitle: string = "CREATE A LISTING";

  const mainMessage: MainMessaging = useSelector(mainMessageSelector);
  const reloadListingId: number = useSelector(reloadListingIdSelector);
  const config: Config = useSelector(configSelector);
  const isDuplicatingListing: boolean = useSelector(
    isDuplicatingListingSelector,
  );
  const selectedCountry: string | undefined = useSelector(
    (state: State) => state.mapping.selectedCountry,
  );
  const listing: Listing = useSelector(currentListingSelector);
  // this code determines if it has an id (create or edit fork)
  let id: string = "";
  const { match } = props;
  if (match && match.params && match.params.id) {
    id = match.params.id;
    navTitle = "EDIT LISTING";
  }

  const anchors: string[] = [
    "team",
    "property",
    "highlights",
    "specifications",
    "spaces",
    "contacts",
  ];

  const {
    miqUpdates,
    isApplyingMiqUpdates,
    isIgnoringMiqUpdates,
    applyMiqUpdates,
    ignoreMiqUpdates,
  } = useMiqSync(id, listing, config);

  // always run once
  useEffect(() => {
    clearPendingImageDeletions(); // discard any stale deletions from a previous listing session
    dispatch(loadCurrentListing(id, config));
    dispatch(setIsDuplicatingListing(false));
    dispatch(loadExternalPublishedCount());
  }, []);

  // re-fetch counts when the selected region changes (e.g. user switches region)
  useEffect(() => {
    if (selectedCountry) {
      dispatch(loadExternalPublishedCount());
    }
  }, [selectedCountry]);

  // run this only when the reload listing id has been changed and is > 0
  useEffect(() => {
    if (reloadListingId > 0) {
      clearPendingImageDeletions(); // discard any stale deletions before loading the next listing
      id = reloadListingId.toString();
      dispatch(loadCurrentListing(id, config, isDuplicatingListing));
      dispatch(setReloadListingId(-1)); // clear out the flag so it can be tripped again on next save (and this doesnt enter an infinite loop)
    }
  }, [reloadListingId]);

  // Remove navTitle if the listing was deleted
  if (listing.isDeleted) {
    navTitle = "";
  }

  const ListingEntryMemo = useMemo(
    () => (
      <ListingEntry
        key={"listing" + new Date().getTime()}
        listing={listing}
        anchors={anchors}
        navTitle={navTitle}
      />
    ),
    [listing, isDuplicatingListing],
  );

  return (
    <LoadingContainer
      isLoading={mainMessage.show ? false : true}
      psuedoEditNav={true}
      navTitle={navTitle}
    >
      {mainMessage.show ? (
        <MainMessage mainMessage={mainMessage} />
      ) : (
        <>
          {ListingEntryMemo}
          {isMiqSyncEnabled(config) && miqUpdates && (
            <MiqSyncReview
              listingUpdates={miqUpdates}
              onApply={applyMiqUpdates}
              onIgnore={ignoreMiqUpdates}
              isApplying={isApplyingMiqUpdates}
              isIgnoring={isIgnoringMiqUpdates}
            />
          )}
        </>
      )}
    </LoadingContainer>
  );
};

export default ListingEntryContainer;
